import React, { useState } from 'react';
import { simpleAuthAPI } from '../services/simpleAuthAPI';

interface SessionRecoveryProps {
  onRecoveryComplete?: () => void;
}

export const SessionRecovery: React.FC<SessionRecoveryProps> = ({ onRecoveryComplete }) => {
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoveryComplete, setRecoveryComplete] = useState(false);
  
  const handleForceLogout = async () => {
    setIsRecovering(true);
    setRecoveryMessage('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/force-logout`, {
        method: 'POST',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        setRecoveryMessage('Your session has been cleared successfully. You can now try logging in again.');
        setRecoveryComplete(true);
        
        // Clear any cached auth state
        localStorage.clear();
        sessionStorage.clear();
        
        // Notify parent component
        if (onRecoveryComplete) {
          setTimeout(() => onRecoveryComplete(), 2000);
        }
      } else {
        setRecoveryMessage('Recovery completed. Please try logging in again.');
        setRecoveryComplete(true);
      }
    } catch (error) {
      console.error('Recovery error:', error);
      setRecoveryMessage('Recovery process completed. Please try logging in again.');
      setRecoveryComplete(true);
    } finally {
      setIsRecovering(false);
    }
  };
  
  const handleRefreshPage = () => {
    window.location.reload();
  };
  
  const handleClearBrowserData = () => {
    // Clear all local storage and session storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear cookies (best effort - domain restrictions may apply)
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    setRecoveryMessage('Browser data cleared. Please refresh the page and try logging in again.');
    setRecoveryComplete(true);
  };
  
  if (recoveryComplete) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto">
        <div className="flex items-center mb-4">
          <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-green-800">Recovery Complete</h3>
        </div>
        
        <p className="text-green-700 mb-4">{recoveryMessage}</p>
        
        <div className="space-y-2">
          <button
            onClick={handleRefreshPage}
            className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Refresh Page & Try Again
          </button>
          
          {onRecoveryComplete && (
            <button
              onClick={onRecoveryComplete}
              className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Return to Login
            </button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
      <div className="flex items-center mb-4">
        <svg className="w-6 h-6 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <h3 className="text-lg font-semibold text-yellow-800">Session Recovery</h3>
      </div>
      
      <p className="text-yellow-700 mb-4">
        If you're having trouble logging in, try these recovery steps:
      </p>
      
      <div className="space-y-3">
        <div className="space-y-2">
          <h4 className="font-medium text-yellow-800">Step 1: Clear Session</h4>
          <p className="text-sm text-yellow-600">
            This will clear your current session and cookies from our servers.
          </p>
          <button
            onClick={handleForceLogout}
            disabled={isRecovering}
            className="w-full bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 disabled:bg-yellow-400 disabled:cursor-not-allowed transition-colors"
          >
            {isRecovering ? 'Clearing Session...' : 'Clear My Session'}
          </button>
        </div>
        
        <div className="border-t border-yellow-200 pt-3">
          <h4 className="font-medium text-yellow-800">Step 2: Clear Browser Data</h4>
          <p className="text-sm text-yellow-600 mb-2">
            If the above doesn't work, clear your browser's stored data for this site.
          </p>
          <button
            onClick={handleClearBrowserData}
            className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Clear Browser Data
          </button>
        </div>
        
        <div className="border-t border-yellow-200 pt-3">
          <h4 className="font-medium text-yellow-800">Still Having Issues?</h4>
          <p className="text-sm text-yellow-600">
            Try using a different browser or incognito/private mode.
          </p>
        </div>
      </div>
      
      {recoveryMessage && (
        <div className="mt-4 p-3 bg-white rounded border">
          <p className="text-sm text-gray-700">{recoveryMessage}</p>
        </div>
      )}
    </div>
  );
};